import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      service: formData.get('service'),
      message: formData.get('message')
    };
    
    try {
      // Invia al backend per gestione email
      const response = await axios.post(`${API}/send-contact-email`, data);
      
      if (response.data.success) {
        alert(`Grazie ${data.firstName}!\n\n✅ Email inviata con successo a:\n• info@staritalia.net\n• Copia a diamonds-hub.com\n\nTi contatteremo presto!`);
        e.target.reset();
      }
      
    } catch (error) {
      console.error('Errore invio email:', error);
      // Fallback per errori di rete
      alert(`Grazie ${data.firstName}!\n\n⚠️ Problema temporaneo di rete.\nContattaci direttamente:\n📞 0587 346342\n📱 WhatsApp: +39 347 703 0346`);
      e.target.reset();
    }
  };

  const handleCallNow = () => {
    window.open('tel:0587346342', '_self');
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefono",
      details: ["0587 346342", "WhatsApp: +39 347 703 0346"],
      color: "text-blue-600"
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@staritalia.net", "assistenza@staritalia.net"],
      color: "text-green-600"
    },
    {
      icon: MapPin,
      title: "Sede Operativa",
      details: ["S.S. Romea 309/63", "44029 Lido di Spina (FE)"],
      color: "text-red-600"
    },
    {
      icon: Clock,
      title: "Orari",
      details: ["Lun-Ven: 8:00-18:00", "Sab: 9:00-13:00"],
      color: "text-purple-600"
    }
  ];

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Siamo al tuo fianco per la miglior soluzione per la{" "}
            <span className="text-blue-600">tua casa</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Contattaci per un preventivo gratuito. I nostri esperti sono pronti 
            ad ascoltare le tue esigenze e offrirti la soluzione perfetta.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Informazioni di contatto
              </h3>
              <div className="grid sm:grid-cols-2 gap-6">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`p-3 rounded-full bg-gray-100 ${info.color}`}>
                          <info.icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">{info.title}</h4>
                          {info.details.map((detail, idx) => (
                            <p key={idx} className="text-gray-600 text-sm">
                              {detail}
                            </p>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Card className="bg-blue-600 text-white border-0">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-4">
                  Richiedi un preventivo gratuito
                </h3>
                <p className="text-blue-100 mb-6">
                  Chiamaci oggi stesso per una consulenza personalizzata 
                  e scopri come possiamo trasformare la tua casa.
                </p>
                <Button
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                  onClick={() => window.open('tel:0587346342', '_self')}
                >
                  Chiamaci ora: 0587 346342
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="shadow-xl border-0">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900">
                Invia un messaggio
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome *
                    </label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      className="w-full"
                      placeholder="Il tuo nome"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Cognome *
                    </label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      required
                      className="w-full"
                      placeholder="Il tuo cognome"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full"
                    placeholder="la-tua-email@esempio.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefono
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="w-full"
                    placeholder="+39 123 456 7890"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Servizio di interesse
                  </label>
                  <select
                    id="service"
                    name="service"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleziona un servizio</option>
                    <option value="rifacimenti">Rifacimenti Bagni-Docce</option>
                    <option value="serramenti">Serramenti Infissi-Porte-Blindati</option>
                    <option value="vetrate">Vetrate Panoramiche</option>
                    <option value="pergole">Pergole e Tende</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Messaggio *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="w-full"
                    placeholder="Descrivi il tuo progetto o le tue esigenze..."
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
                >
                  Invia messaggio
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  * Campi obbligatori. Rispettiamo la tua privacy e non condivideremo 
                  mai i tuoi dati con terze parti.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;